import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import { FLUSH, PAUSE, PERSIST, persistStore, PURGE, REGISTER, REHYDRATE } from "redux-persist";
import rootReducer from "../reducers";
import { corePlusIncentivesApi } from "../services/CorePlusIncentives/corePlusIncentivesApi";
import { accAPI } from "../services/index";
import { mybizShellAPI } from "../services/MyBizShell/mybizShellAPI";
import { savedKpiPreferencesAPI } from "../services/KPIPreferences/savedKpiPreferencesAPI";
import { frontlineGroupGrowthAPI } from "../services/GroupGrowth/FrontlineGroupGrowth/frontlineGroupGrowthAPI";
import { ssiAPI } from "../services/StrongStart/ssiAPI";
import { ssiV2API } from "../services/StrongStart/ssiV2API";
import { lsiAPI } from "../services/LeadershipSeminar/leadershipSeminarAPI";
import { personalGroupGrowthAPI } from "../services/GroupGrowth/PersonalGroupGrowth/personalGroupGrowthAPI";
import { pggGcrAPI } from "../services/GroupGrowth/PersonalGroupGrowthGCR/personalGroupGrowthGCRAPI";
import { performancePlusEliteAPI } from "../services/PerformancePlusElite/performancePlusEliteAPI";
import { ssiUplineApi } from "../services/SSIUpline/ssiUplineAPI";
import { legsTrackingAPI } from "../services/HigherAwardTracking/LegsTracking/legsTrackingAPI";
import { monthlyPGGAPI } from "../services/GroupGrowth/MonthlyPersonalGroupGrowth/monthlyPGGAPI";
import { higherAwardTrackingAPI } from "../services/HigherAwardTracking/HigherAwardTrackingAPI";
import { qcgAPI } from "../services/GroupGrowth/QcGrowth/qcGrowthAPI";
import { bronzeGroupTrackingAPI } from "../services/BronzeGroupTracking/bronzeGroupTracking";
import { ttciAPI } from "../services/TwoTimeCashIncentives/TwoTimeCashIncentives";
import { csiAPI } from "../services/CustomerSalesIncentive/CustomerSalesIncentive";
import { visionBonusAPI } from "../services/VisionBonus/VisionBonusAPI";
import { monthlyQAPI } from "../services/MonthlyQ/monthlyQAPI";
import { frontlineQAPI } from "../services/FrontlineQ/frontlineQAPI";
import { actionReportsApi } from "../services/ActionReports/ActionReportAPI";
import "../services/ActionReports/WatchlistBff";
import { aboEnrichmentAPI } from "../services/AboEnrichment/AboEnrichmentAPI";
import { qMonthHistoryApi } from "../services/QMonthHistory/qMonthHistoryAPI";
import { treeViewApi } from "../services/TreeView/TreeViewAPI";
import { academyAPI } from "../services/Academy/academyAPI";
import { groupManagementOverviewAPI } from "../services/GroupManagementOverview/groupManagementOverviewAPI";
import { dataviewAPI } from "../services/Dataview/DataviewAPI";
import { annualIncomeDashboardAPI } from "../services/AnnualIncomeDashboard/annualIncomeDashboardAPI";
import { aboProfileAPI } from "../services/AboProfile/AboProfileAPI";
import { profileImageAPI } from "../services/ActionReports/ProfileImageAPI";

// https://stackoverflow.com/questions/66315413/type-definitions-for-redux-toolkit-store-with-preloadedstate
export type RootState = ReturnType<typeof rootReducer>;

export const setupStore = (preloadedState?: Partial<RootState>) =>
  configureStore({
    reducer: rootReducer,
    ...preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          ignoredActionPaths: ["meta.arg", "meta.baseQueryMeta", "payload.previousElement"],
          ignoredPaths: ["profile.previousElement"],
        },
      })
        .concat(corePlusIncentivesApi.middleware)
        .concat(accAPI.middleware)
        .concat(mybizShellAPI.middleware)
        .concat(savedKpiPreferencesAPI.middleware)
        .concat(frontlineGroupGrowthAPI.middleware)
        .concat(personalGroupGrowthAPI.middleware)
        .concat(pggGcrAPI.middleware)
        .concat(ssiAPI.middleware)
        .concat(ssiV2API.middleware)
        .concat(lsiAPI.middleware)
        .concat(qcgAPI.middleware)
        .concat(monthlyPGGAPI.middleware)
        .concat(performancePlusEliteAPI.middleware)
        .concat(ssiUplineApi.middleware)
        .concat(bronzeGroupTrackingAPI.middleware)
        .concat(ttciAPI.middleware)
        .concat(csiAPI.middleware)
        .concat(visionBonusAPI.middleware)
        .concat(legsTrackingAPI.middleware)
        .concat(higherAwardTrackingAPI.middleware)
        .concat(monthlyQAPI.middleware)
        .concat(frontlineQAPI.middleware)
        .concat(actionReportsApi.middleware)
        .concat(aboEnrichmentAPI.middleware)
        .concat(qMonthHistoryApi.middleware)
        .concat(treeViewApi.middleware)
        .concat(academyAPI.middleware)
        .concat(groupManagementOverviewAPI.middleware)
        .concat(dataviewAPI.middleware)
        .concat(annualIncomeDashboardAPI.middleware)
        .concat(aboProfileAPI.middleware)
        .concat(profileImageAPI.middleware),
  });

export type Store = ReturnType<typeof setupStore>;
export type AppDispatch = Store["dispatch"];

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// setup store
export const store = setupStore();

// persist redux store so that the state gets rehydrated on page refresh
export const persistor = persistStore(store);

// Expose store on window for e2e diagnostics
(window as any).store = store;

// Sync period changes to acc-boot store for Lit components
store.subscribe(() => {
  const state = store.getState();
  const selectedPeriod = state.period.selectedPeriod;

  // Sync to acc-boot store if it exists (used by Lit web components)
  if (typeof window !== "undefined" && (window as any).accBoot?.store) {
    const accBootStore = (window as any).accBoot.store;
    const currentAccBootState = accBootStore.getState();

    // Only update if the period has changed to avoid unnecessary updates
    if (currentAccBootState.bonusPeriod !== selectedPeriod) {
      accBootStore.setState({ bonusPeriod: selectedPeriod });
    }

    // Also sync user data if it's missing or changed
    const reactUser = state.boot.user;
    const accBootUser = currentAccBootState.user;
    if (!accBootUser?.access_token || accBootUser.access_token !== reactUser.access_token) {
      accBootStore.setState({ user: reactUser });
    }
  }
});
